import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { Box, LinearProgress } from '@mui/material';
import CreativeItem from './CreativeItem';
import CreativeEmptyView from '../../creatives/components/CreativeEmptyView';
import { useFBCreatives } from '../api';
import type { FacebookCreativeAdByRegionDTO } from '../api/types';
import useInitMasonryColumns from 'hooks/useInitMasonryColumns';
import { format } from 'date-fns';
import useFacebookAdsLibraryParams from '../hooks/useFacebookAdsLibraryParams';

const PAGE_SIZE = 50;

interface IProps {
  resetFilters: () => void;
}

const FacebookAdsCreativeView: FC<IProps> = ({ resetFilters }) => {
  const {
    adTypes,
    languages,
    ordering,
    branches,
    pageName,
    startDate,
    endDate,
    company,
    domains,
    flows,
    isRunning,
    newOnly,
    includeWelltech,
    network,
    region,
  } = useFacebookAdsLibraryParams();

  const {
    data: { pages, count },
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useFBCreatives({
    date_from: format(startDate, 'yyyy-MM-dd'),
    date_to: format(endDate, 'yyyy-MM-dd'),
    desc: ordering.split(':')[1] === 'desc',
    limit: PAGE_SIZE,
    order_by: ordering.split(':')[0],
    offset: 0,
    branch_id: branches,
    page_id: pageName,
    company_id: company,
    domain: domains,
    flow: flows,
    language: languages,
    ad_creative_type: adTypes,
    running: isRunning,
    new_only: newOnly,
    include_welltech: includeWelltech,
    network,
    region,
  });

  const { columns } = useInitMasonryColumns();

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage().catch(console.error);
        }
      },
      { root: document.querySelector('#creatives-ads-page'), rootMargin: '600px' }
    );

    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const creativePageContainer = document.querySelector('#creatives-ads-page');
    if (creativePageContainer) creativePageContainer.scrollTop = 0;
  }, [ordering, adTypes, languages, branches, pageName, startDate, endDate, company, domains, flows, isRunning, newOnly, includeWelltech, network, region]);

  // Round-robin distribution into fixed columns: item i always lands in column i % columns
  // at a stable position, so newly fetched pages only append to the bottom of each column —
  // existing items never move and the layout fills continuously without seams or rebuilds.
  const columnBuckets: FacebookCreativeAdByRegionDTO[][] = Array.from({ length: columns }, () => []);
  const allCreatives: FacebookCreativeAdByRegionDTO[] = pages.flat();
  allCreatives.forEach((creative, index) => {
    columnBuckets[index % columns].push(creative);
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {count ? (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
          {columnBuckets.map((bucket, columnIndex) => (
            <Box
              key={`fb-creatives-column-${columnIndex}`}
              sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}
            >
              {bucket.map((c) => (
                <CreativeItem key={c.ad_creative_id} creative={c} />
              ))}
            </Box>
          ))}
        </Box>
      ) : (
        <CreativeEmptyView reset={resetFilters} />
      )}
      <Box ref={sentinelRef} sx={{ height: 1 }} />
      {isFetchingNextPage ? (
        <Box sx={{ py: 2 }}>
          <LinearProgress />
        </Box>
      ) : null}
    </Box>
  );
};

export default FacebookAdsCreativeView;
